/**
 * CurrencyList Component
 *
 * This component renders a list of selectable currencies used within
 * the table footer features (e.g., Expand Sum mode).
 * It displays all available currencies from the Redux store and optionally
 * toggles visibility based on the expand-currency menu state.
 *
 * Props:
 *  - `currencyMenuHandler` (function, optional):
 *        A callback invoked when a currency item is clicked.
 *        Typically used to close the dropdown menu after a selection.
 *
 *  - `shouldExpandCurrencies` (boolean, optional):
 *        When true, the currency list is shown with the `"open"` CSS class.
 *        When false or undefined, the list is collapsed.
 *
 * State (via Redux):
 *  - `availableCurrencies`: Array of currency codes available for aggregation
 *        (e.g., ["USD", "EUR", "GBP"]). Retrieved from `state.stats.availableCurrencies`.
 *
 * useLayoutEffect dynamically adjusts the height of a dropdown menu when its visibility changes
 * or when the list of available items changes. It ensures smooth animation and prevents layout shifts
 * by setting the height based on the content.
 *
 * Behavior:
 *  - The component automatically maps over all available currencies, rendering
 *    a `CurrencyItem` for each one.
 *  - Adds the `"open"` class when the dropdown is expanded, allowing the menu
 *    to be animated or styled differently.
 *  - Marks the first currency item with `isFirstItem={true}`, which can be
 *    useful for applying top-border overrides or rounded corners.
 *
 * Usage Example:
 *   <CurrencyList
 *     shouldExpandCurrencies={isOpen}
 *     currencyMenuHandler={handleToggle}
 *   />
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import CurrencyItem from './CurrencyItem';
import { useAppSelector } from '../../../../../../hook/useAppSelector';

const CurrencyList: React.FC = () => {
  const availableCurrencies = useAppSelector((state) => state.stats.availableCurrencies);
  const listRef = useRef<HTMLUListElement>(null);
  const [height, setHeight] = useState(0);
  const shouldViewCurrencies = useAppSelector((state) => state.stats.shouldViewCurrencies);

  useLayoutEffect(() => {
    if (shouldViewCurrencies && listRef.current) {
      setHeight(listRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [shouldViewCurrencies, availableCurrencies.length]);

  return (
    <ul ref={listRef} style={{ height }} className={`currency_list ${shouldViewCurrencies ? 'open' : ''}`}>
      {availableCurrencies.map((currency, index) => (
        <CurrencyItem key={currency} currencyItem={currency} isFirstItem={index === 0} />
      ))}
    </ul>
  );
};

export default CurrencyList;
